import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react'
import {
  AlertCircle,
  CalendarCheck,
  CalendarClock,
  Check,
  CheckCircle2,
  Copy,
  Download,
  FilePen,
  Loader2,
  Mail,
  MessageSquare,
  Phone,
  Plane,
  Share2,
  Zap,
} from 'lucide-react'
import {
  statsService,
} from '../services/api'
import {
  useAuth,
} from '../hooks/useAuth'
import type {
  AssistantStatsResponse,
} from '../client'
import {
  trackEvent,
} from '../analytics/posthog'

// Public marketing site — what we point people to when a user shares. The
// stats page itself is behind auth, so the share link goes to the product,
// not the private numbers.
const MARKETING_URL = 'https://catchagent.ai'

// White Catch symbol (mirrors public/images/logo/symbol-white.svg). Inlined
// with explicit width/height so it rasterises reliably onto the share-image
// canvas across browsers (a bare viewBox has no intrinsic size in Firefox).
const CATCH_SYMBOL_WHITE = `<svg xmlns="http://www.w3.org/2000/svg" width="220" height="179" viewBox="0 0 2458.89 2000"><path fill="#faf7f7" d="M1938.34,813.04c-21.55,29.95-50.52,53.34-109.03,53.35-58.52,0-391.68,0-391.68,0-17.2-54.78-47.34-103.83-86.84-143.57l525.28-.21c16.81.22,32.02,4.53,45.75,11.9,29.07,15.6,36.61,50.62,16.52,78.54ZM1285.14,1111.74c44.32-60.12,32.26-215.11-129.79-95.27-199.22,147.32-294.31,154.49-313.71,98.87-20.79-59.6,16.66-158.01,54.22-226.1,64.65-117.22,188.93-206.45,321.69-205.39h122.28c72.38,0,377.29-.47,448.32,0,71.03.47,106.21-28.4,132.37-64.77,24.39-33.9,15.24-76.42-20.06-95.36-16.67-8.94-35.13-14.17-55.55-14.44l-784.9.32c-274.65-7.08-468.85,101.51-544.18,588.68-42.65,275.83,123.88,542.64,465.4,293.19,242.55-177.16,255.46-214.03,303.9-279.73ZM1843.36,917.18c-13.73-7.37-28.94-11.67-45.75-11.9l-350.16.14c4.18,21.77,6.39,44.24,6.39,67.22,0,26.29-2.89,51.9-8.32,76.56,98.59-.05,259.91-.25,305.32-.14,57.91.14,87.48-23.39,109.03-53.35,20.09-27.92,12.55-62.94-16.52-78.54ZM1716.19,1099.86c-13.73-7.37-28.94-11.67-45.75-11.9l-235.83.1c-19.09,55.46-51.56,104.67-93.55,143.83,98.31-.11,239.63,0,282.62-.14,58.51-.18,87.48-23.39,109.03-53.35,20.09-27.92,12.55-62.94-16.52-78.54Z"/></svg>`

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = (): void => resolve(img)
    img.onerror = reject
    img.src = src
  })
}

type StatItem = {
  key: string
  label: string
  value: number
  suffix?: string | undefined
  Icon: typeof Mail
}

function formatNumber(value: number): string {
  return value.toLocaleString('en-US')
}

function formatMemberSince(iso: string): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
}

// Headline number: the real actions the assistant took on the user's behalf.
// Messages handled are the user's own prompts (engagement, not assistant
// output), so they're shown as a stat but left out of this total.
function totalActions(stats: AssistantStatsResponse): number {
  return (
    stats.emails_sent
    + stats.emails_drafted
    + stats.meetings_scheduled
    + stats.meetings_rescheduled
    + stats.tasks_completed
    + stats.phone_calls_handled
    + stats.trips_booked
  )
}

function buildShareText(stats: AssistantStatsResponse): string {
  const parts: string[] = []
  if (stats.emails_sent > 0) parts.push(`${formatNumber(stats.emails_sent)} emails sent`)
  if (stats.emails_drafted > 0) {
    parts.push(`${formatNumber(stats.emails_drafted)} emails drafted`)
  }
  if (stats.meetings_scheduled > 0) {
    parts.push(`${formatNumber(stats.meetings_scheduled)} meetings scheduled`)
  }
  if (stats.tasks_completed > 0) {
    parts.push(`${formatNumber(stats.tasks_completed)} tasks done`)
  }
  const highlights = parts.slice(0, 3).join(', ')
  const total = totalActions(stats)
  const lead = total > 0
    ? `My Catch assistant handled ${formatNumber(total)} things for me`
    : 'Catch is my AI chief of staff'
  const tail = highlights ? ` — ${highlights}.` : '.'
  return `${lead}${tail} Meet your AI chief of staff 👉`
}

/**
 * Renders the shareable stat card to a 1080×1080 PNG on an off-screen canvas
 * (no external dependency), returning it as a Blob. Drawn by hand so the image
 * is self-contained and pixel-stable across browsers.
 */
async function renderShareImage(stats: AssistantStatsResponse): Promise<Blob | null> {
  const size = 1080
  const canvas = document.createElement('canvas')
  canvas.width = size
  canvas.height = size
  const ctx = canvas.getContext('2d')
  if (!ctx) return null

  // Brand background: deep charcoal with a warm red glow.
  ctx.fillStyle = '#212121'
  ctx.fillRect(0, 0, size, size)
  const glow = ctx.createRadialGradient(size / 2, 210, 40, size / 2, 210, 620)
  glow.addColorStop(0, 'rgba(229, 80, 80, 0.55)')
  glow.addColorStop(1, 'rgba(229, 80, 80, 0)')
  ctx.fillStyle = glow
  ctx.fillRect(0, 0, size, size)

  ctx.textAlign = 'center'

  // Catch logo (falls back to the wordmark text if the SVG can't rasterise).
  try {
    const logo = await loadImage(
      `data:image/svg+xml;utf8,${encodeURIComponent(CATCH_SYMBOL_WHITE)}`,
    )
    const logoW = 150
    const logoH = logoW * (2000 / 2458.89)
    ctx.drawImage(logo, (size - logoW) / 2, 74, logoW, logoH)
  } catch {
    ctx.fillStyle = '#FAF7F7'
    ctx.font = '600 44px Inter, system-ui, sans-serif'
    ctx.fillText('Catch', size / 2, 150)
  }

  // Eyebrow.
  ctx.fillStyle = 'rgba(250, 247, 247, 0.7)'
  ctx.font = '500 30px Inter, system-ui, sans-serif'
  ctx.fillText('MY ASSISTANT, BY THE NUMBERS', size / 2, 210)

  // Hero: total things the assistant handled.
  ctx.fillStyle = '#E55050'
  ctx.font = '700 210px Inter, system-ui, sans-serif'
  ctx.fillText(formatNumber(totalActions(stats)), size / 2, 470)
  ctx.fillStyle = '#FAF7F7'
  ctx.font = '600 46px Inter, system-ui, sans-serif'
  ctx.fillText('things handled', size / 2, 540)

  // Three headline metrics in a row.
  const metrics: Array<[string, number]> = [
    ['Emails', stats.emails_sent],
    ['Meetings', stats.meetings_scheduled],
    ['Tasks', stats.tasks_completed],
  ]
  const colWidth = size / metrics.length
  metrics.forEach(([label, value], i) => {
    const cx = colWidth * i + colWidth / 2
    ctx.fillStyle = '#FAF7F7'
    ctx.font = '700 88px Inter, system-ui, sans-serif'
    ctx.fillText(formatNumber(value), cx, 760)
    ctx.fillStyle = 'rgba(250, 247, 247, 0.65)'
    ctx.font = '500 34px Inter, system-ui, sans-serif'
    ctx.fillText(label, cx, 815)
  })

  // Footer CTA.
  ctx.fillStyle = 'rgba(250, 247, 247, 0.85)'
  ctx.font = '500 38px Inter, system-ui, sans-serif'
  ctx.fillText('Meet your AI chief of staff', size / 2, 960)
  ctx.fillStyle = '#E55050'
  ctx.font = '600 40px Inter, system-ui, sans-serif'
  ctx.fillText('catchagent.ai', size / 2, 1015)

  return await new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), 'image/png')
  })
}

function triggerImageDownload(blob: Blob): void {
  const objectUrl = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = objectUrl
  anchor.download = 'my-catch-stats.png'
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  URL.revokeObjectURL(objectUrl)
}

function StatCard({ label, value, suffix, Icon }: Omit<StatItem, 'key'>): JSX.Element {
  return (
    <div className="group bg-white rounded-2xl border border-gray-200 shadow-soft p-5 sm:p-6 transition-shadow hover:shadow-soft-lg">
      <div className="flex items-center justify-between">
        <div className="w-11 h-11 rounded-xl bg-primary-50 text-primary-600 flex items-center justify-center">
          <Icon className="w-5 h-5" strokeWidth={2} />
        </div>
      </div>
      <div className="mt-4">
        <div className="text-3xl sm:text-4xl font-bold text-gray-900 tracking-tight tabular-nums">
          {formatNumber(value)}
          {suffix ? <span className="text-lg font-semibold text-gray-400 ml-1">{suffix}</span> : null}
        </div>
        <div className="mt-1 text-sm font-medium text-gray-500">{label}</div>
      </div>
    </div>
  )
}

function ShareButton({ label, onClick, children, className }: {
  label: string
  onClick: () => void
  children: React.ReactNode
  className?: string
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className={`flex items-center justify-center w-11 h-11 rounded-full text-white transition-transform duration-200 active:scale-90 shadow-soft ${className ?? ''}`}
    >
      {children}
    </button>
  )
}

export function StatsPage(): JSX.Element {
  const { user } = useAuth()
  const [stats, setStats] = useState<AssistantStatsResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [copied, setCopied] = useState(false)
  const [downloading, setDownloading] = useState(false)
  const copyResetRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(false)
    statsService
      .getMyStats()
      .then((data) => {
        if (!cancelled) setStats(data)
      })
      .catch(() => {
        if (!cancelled) setError(true)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => () => {
    if (copyResetRef.current) clearTimeout(copyResetRef.current)
  }, [])

  const shareText = useMemo(() => (stats ? buildShareText(stats) : ''), [stats])

  const openShare = useCallback((channel: string, href: string) => {
    trackEvent('stats_shared', { channel })
    window.open(href, '_blank', 'noopener,noreferrer')
  }, [])

  const shareTwitter = useCallback(() => {
    const href = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(MARKETING_URL)}`
    openShare('twitter', href)
  }, [openShare, shareText])

  const shareLinkedIn = useCallback(() => {
    const href = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(MARKETING_URL)}`
    openShare('linkedin', href)
  }, [openShare])

  const shareFacebook = useCallback(() => {
    const href = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(MARKETING_URL)}`
    openShare('facebook', href)
  }, [openShare])

  const shareWhatsApp = useCallback(() => {
    const href = `https://wa.me/?text=${encodeURIComponent(`${shareText} ${MARKETING_URL}`)}`
    openShare('whatsapp', href)
  }, [openShare, shareText])

  const downloadImage = useCallback(async () => {
    if (!stats) return
    setDownloading(true)
    try {
      const blob = await renderShareImage(stats)
      if (!blob) return
      triggerImageDownload(blob)
      trackEvent('stats_shared', { channel: 'download' })
    } finally {
      setDownloading(false)
    }
  }, [stats])

  // Instagram has no web share intent — download the card, then post it.
  const shareInstagram = useCallback(async () => {
    trackEvent('stats_shared', { channel: 'instagram' })
    await downloadImage()
  }, [downloadImage])

  const nativeShare = useCallback(async () => {
    if (!stats) return
    const nav = navigator as Navigator & {
      canShare?: (data?: ShareData) => boolean
    }
    // No Web Share API — fall back to the download path (renders once there).
    if (!nav.share) {
      await downloadImage()
      return
    }
    const text = `${shareText} ${MARKETING_URL}`
    const blob = await renderShareImage(stats)
    const file = blob ? new File([blob], 'my-catch-stats.png', { type: 'image/png' }) : null
    try {
      if (file && nav.canShare?.({ files: [file] })) {
        await nav.share({ title: 'My Catch stats', text, files: [file] })
      } else {
        await nav.share({ title: 'My Catch stats', text })
      }
      trackEvent('stats_shared', { channel: 'native' })
    } catch {
      // User dismissed the share sheet — nothing to do.
    }
  }, [stats, shareText, downloadImage])

  const copyCaption = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(`${shareText} ${MARKETING_URL}`)
      setCopied(true)
      trackEvent('stats_shared', { channel: 'copy' })
      if (copyResetRef.current) clearTimeout(copyResetRef.current)
      copyResetRef.current = setTimeout(() => setCopied(false), 2000)
    } catch {
      // Clipboard blocked — no-op.
    }
  }, [shareText])

  const statItems: StatItem[] = useMemo(() => {
    if (!stats) return []
    return [
      { key: 'emails', label: 'Emails sent', value: stats.emails_sent, Icon: Mail },
      { key: 'drafts', label: 'Emails drafted', value: stats.emails_drafted, Icon: FilePen },
      { key: 'meetings', label: 'Meetings scheduled', value: stats.meetings_scheduled, Icon: CalendarCheck },
      { key: 'tasks', label: 'Tasks completed', value: stats.tasks_completed, Icon: CheckCircle2 },
      { key: 'messages', label: 'Messages handled', value: stats.messages_handled, Icon: MessageSquare },
      { key: 'reschedules', label: 'Meetings rescheduled', value: stats.meetings_rescheduled, Icon: CalendarClock },
      { key: 'calls', label: 'Calls handled', value: stats.phone_calls_handled, suffix: stats.phone_call_minutes > 0 ? `· ${formatNumber(stats.phone_call_minutes)} min` : undefined, Icon: Phone },
      { key: 'trips', label: 'Trips booked', value: stats.trips_booked, Icon: Plane },
    ]
  }, [stats])

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-primary-500 animate-spin" />
      </div>
    )
  }

  if (error || !stats) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-4">
        <AlertCircle className="w-10 h-10 text-primary-500 mb-3" />
        <h1 className="text-lg font-semibold text-gray-900">We couldn't load your stats</h1>
        <p className="mt-1 text-sm text-gray-500">Please refresh the page and try again.</p>
      </div>
    )
  }

  const memberSince = formatMemberSince(stats.member_since)
  const displayName = stats.user_name || user?.name || ''

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <div className="container mx-auto px-4 py-8 sm:py-12 max-w-4xl">
        {/* Header */}
        <div className="text-center mb-8 animate-fade-in">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 tracking-tight">
            {displayName ? `${displayName.split(' ')[0]}, here's your impact` : 'Your impact'}
          </h1>
          <p className="mt-2 text-gray-500">
            Everything your Catch assistant has handled for you
            {memberSince ? ` since ${memberSince}` : ''}.
          </p>
        </div>

        {/* Hero: total things handled */}
        <div className="relative overflow-hidden rounded-3xl bg-catch-black text-white shadow-soft-lg p-8 sm:p-12 mb-6 animate-slide-up">
          <div
            className="pointer-events-none absolute -top-24 left-1/2 -translate-x-1/2 w-[36rem] h-[36rem] rounded-full opacity-40 blur-3xl"
            style={{ background: 'radial-gradient(circle, #E55050 0%, transparent 60%)' }}
          />
          <div className="relative text-center">
            <img
              src="/images/logo/symbol-white.svg"
              alt="Catch"
              className="mx-auto mb-5 h-8 w-auto opacity-90"
            />
            <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-white/60">
              <Zap className="w-4 h-4" />
              Things handled for you
            </div>
            <div className="mt-3 text-6xl sm:text-8xl font-bold tracking-tight text-primary-500 tabular-nums">
              {formatNumber(totalActions(stats))}
            </div>
            <p className="mt-3 text-white/70 max-w-md mx-auto">
              Every email, meeting, task, call, and trip your assistant took off your plate.
            </p>
          </div>
        </div>

        {/* Stat grid */}
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
          {statItems.map((item) => (
            <StatCard
              key={item.key}
              label={item.label}
              value={item.value}
              suffix={item.suffix}
              Icon={item.Icon}
            />
          ))}
        </div>

        {/* Share */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-soft p-6 sm:p-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Share2 className="w-5 h-5 text-primary-500" />
                Share your stats
              </h2>
              <p className="mt-1 text-sm text-gray-500">
                Show off what your assistant gets done. Post it anywhere.
              </p>
            </div>
            <div className="flex items-center gap-2.5">
              <ShareButton label="Share on X" onClick={shareTwitter} className="bg-black hover:bg-gray-800">
                <XLogo />
              </ShareButton>
              <ShareButton label="Share on LinkedIn" onClick={shareLinkedIn} className="bg-[#0A66C2] hover:brightness-110">
                <LinkedInLogo />
              </ShareButton>
              <ShareButton label="Share on Facebook" onClick={shareFacebook} className="bg-[#1877F2] hover:brightness-110">
                <FacebookLogo />
              </ShareButton>
              <ShareButton label="Share on WhatsApp" onClick={shareWhatsApp} className="bg-[#25D366] hover:brightness-110">
                <WhatsAppLogo />
              </ShareButton>
              <ShareButton label="Share on Instagram" onClick={shareInstagram} className="bg-gradient-to-tr from-[#F58529] via-[#DD2A7B] to-[#8134AF] hover:brightness-110">
                <InstagramLogo />
              </ShareButton>
            </div>
          </div>

          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <button
              type="button"
              onClick={downloadImage}
              disabled={downloading}
              className="flex-1 inline-flex items-center justify-center gap-2 rounded-xl bg-primary-600 text-white px-4 py-2.5 text-sm font-medium hover:bg-primary-700 transition-colors disabled:opacity-60"
            >
              {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              Download image
            </button>
            <button
              type="button"
              onClick={nativeShare}
              className="flex-1 inline-flex items-center justify-center gap-2 rounded-xl bg-gray-900 text-white px-4 py-2.5 text-sm font-medium hover:bg-gray-800 transition-colors"
            >
              <Share2 className="w-4 h-4" />
              Share…
            </button>
            <button
              type="button"
              onClick={copyCaption}
              className="flex-1 inline-flex items-center justify-center gap-2 rounded-xl border border-gray-300 text-gray-700 px-4 py-2.5 text-sm font-medium hover:bg-gray-50 transition-colors"
            >
              {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
              {copied ? 'Copied' : 'Copy caption'}
            </button>
          </div>
        </div>

        <p className="mt-6 text-center text-xs text-gray-400">
          Stats update as your assistant works. Numbers reflect activity attributed to Catch.
        </p>
      </div>
    </div>
  )
}

/* --- Brand marks (inline SVG so we control the exact, current logos) --- */

function XLogo(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor" aria-hidden="true">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24h-6.66l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231 5.45-6.231Zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77Z" />
    </svg>
  )
}

function LinkedInLogo(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor" aria-hidden="true">
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286ZM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065Zm1.782 13.019H3.555V9h3.564v11.452ZM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003Z" />
    </svg>
  )
}

function FacebookLogo(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor" aria-hidden="true">
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073Z" />
    </svg>
  )
}

function WhatsAppLogo(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor" aria-hidden="true">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z" />
    </svg>
  )
}

function InstagramLogo(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor" aria-hidden="true">
      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069ZM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0Zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324ZM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8Zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881Z" />
    </svg>
  )
}
